import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

import Icons, { IconSizes } from '../Icons';

import Autocomplete from '../Select/Autocomplete';
import { InputHeights } from '../../utils/consts';
import { Themes } from '../../style/theme-consts';
import * as S from './styled';

/**
 * SearchAutocomplete component extends the existing Autocomplete
 * with search functionality and API fetching capability
 */
const SearchAutocomplete = ({
  // Props specific to SearchAutocomplete
  onSearch,
  fetchOptions,
  debounceTime = 300,

  // Props passed to base Autocomplete
  options = [],
  multiple = false,
  onChange,
  onBlur,
  onInputChange: propOnInputChange,
  onClose,
  value,
  label,
  width = '100%',
  height = InputHeights.MEDIUM,
  minHeight,
  maxPopperHeight,
  placeholder,
  mode = Themes.LIGHT,
  disablePortal = true,
  isOptionEqualToValue,
  filterOptions,
  clearOnBlur,
  blurOnSelect,
  noOptionsText,
  freeSolo = true, // Default to true for search
  loading: propLoading,
  testId = 'search-autocomplete',
  clearable = true,
  disabled,
  error,
  helperText,
  withSearchIcon = true,
  ...otherProps
}) => {
  const [inputValue, setInputValue] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [localOptions, setLocalOptions] = useState(options);
  const [open, setOpen] = useState(false);
  // Combined loading state from props and local state
  const loading = propLoading || localLoading;

  // Custom filter function to check if there are any matches
  // eslint-disable-next-line no-shadow
  const filterOptionsAndManagePopper = (opts, { inputValue }) => {
    const filtered = opts.filter(option => option.label?.toLowerCase().includes(inputValue.toLowerCase()));

    // Only open the popper if there are matches AND inputValue is not empty
    if (inputValue.length > 0) {
      setOpen(filtered.length > 0);
    }

    return filtered;
  };

  // Handle option fetching
  const fetchData = useCallback(
    async searchText => {
      if (!fetchOptions) return;

      setLocalLoading(true);
      try {
        const result = await fetchOptions(searchText);
        setLocalOptions(result);
      } catch (e) {
        setLocalOptions([]);
      } finally {
        setLocalLoading(false);
      }
    },
    [fetchOptions]
  );

  // Debounced fetch
  const debouncedFetch = useCallback(
    debounce(text => {
      fetchData(text);
    }, debounceTime),
    [fetchData, debounceTime]
  );

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Update local options when props options change
  useEffect(() => {
    if (options && options.length > 0) {
      setLocalOptions(options);
    }
  }, [options]);

  // Handle search action
  const handleSearch = () => {
    if (onSearch && inputValue.trim() !== '') {
      onSearch(inputValue);
    }
  };

  // Handle key press for search
  const handleKeyPress = event => {
    // Skip Enter key handling if options menu is open
    const autocompleteElement = document.querySelector(`[data-testid="${testId}"]`);
    const isMenuOpen = autocompleteElement?.classList.contains('Mui-expanded');
    if (event.key === 'Enter' && !isMenuOpen) {
      handleSearch();
      event.preventDefault();
    }
  };

  // Handle input change
  const inputChangeHandler = (event, newInputValue) => {
    setInputValue(newInputValue);

    // If input is cleared, also clear the value
    if (newInputValue === '' && value) {
      const emptyValue = multiple ? [] : '';
      onChange && onChange(emptyValue);
    }

    if (propOnInputChange) {
      propOnInputChange(event, newInputValue);
    }

    if (fetchOptions && newInputValue.trim() !== '') {
      debouncedFetch(newInputValue);
    }
  };

  // Custom clear handler
  const handleClear = e => {
    e.stopPropagation();

    // Clear input value
    setInputValue('');

    // Clear selected value
    if (onChange) {
      const emptyValue = multiple ? [] : '';
      onChange(emptyValue, { isClear: true });
    }

    // Focus the input after clearing (helps with MUI Autocomplete state management)
    setTimeout(() => {
      const input = document.querySelector(`[data-testid="${testId}"] input`);
      if (input) {
        input.value = '';
        input.focus();
      }
    }, 0);
  };

  // Custom render input to add search button and custom clear button
  const renderInputFunc = params => {
    // Override the inputProps to ensure our inputValue is used
    const inputProps = {
      ...params.inputProps,
      value: inputValue
    };

    const textFieldInputProps = {
      InputProps: {
        ...params.InputProps,
        endAdornment: (
          <>
            {loading ? (
              <S.StyledCircularProgress size={20} />
            ) : (
              <InputAdornment position="end">
                {inputValue && clearable && (
                  <IconButton
                    aria-label="clear"
                    onClick={handleClear}
                    size="small"
                    data-testid={`${testId}-clear-button`}>
                    <S.StyledClearIcon fontSize="small" />
                  </IconButton>
                )}
                {withSearchIcon && (
                  <S.SearchIconButton
                    data-testid={`${testId}-search-button`}
                    aria-label="search-btn"
                    onClick={handleSearch}>
                    <Icons.Search size={IconSizes.S} color="var(--input-accent-icon-color, var(--mainGreen))" />
                  </S.SearchIconButton>
                )}
              </InputAdornment>
            )}
            {/* Hide standard autocomplete endAdornment icons */}
          </>
        )
      }
    };

    return (
      <TextField
        {...params}
        inputProps={inputProps}
        size="small"
        variant="outlined"
        label={label}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        data-testid={testId}
        onKeyDown={handleKeyPress}
        {...textFieldInputProps}
      />
    );
  };

  const onCloseHandler = () => {
    setOpen(false);
  };

  const onOpenHandler = () => {
    const filtered = localOptions.filter(option => option.label?.toLowerCase().includes(inputValue.toLowerCase()));
    if (filtered.length > 0) {
      setOpen(true);
    }
  };

  return (
    <S.Wrapper>
      <Autocomplete
        // Pass along all standard Autocomplete props
        options={localOptions}
        onChange={onChange}
        onBlur={onBlur}
        onInputChange={inputChangeHandler}
        onClose={onCloseHandler}
        onOpen={onOpenHandler}
        open={open}
        value={value}
        label={label}
        width={width}
        height={height}
        minHeight={minHeight}
        maxPopperHeight={maxPopperHeight}
        placeholder={placeholder}
        mode={mode}
        disablePortal={disablePortal}
        isOptionEqualToValue={isOptionEqualToValue}
        filterOptions={filterOptionsAndManagePopper}
        clearOnBlur={clearOnBlur}
        blurOnSelect={blurOnSelect}
        noOptionsText={noOptionsText}
        freeSolo={freeSolo}
        loading={loading}
        testId={testId}
        disabled={disabled}
        error={error}
        helperText={helperText}
        // Override clearable to false so we can handle it ourselves
        clearable={false}
        // Force the inputValue to be controlled by our state
        inputValue={inputValue}
        // Search-specific overrides
        renderInput={renderInputFunc}
        // Any other props
        {...otherProps}
      />
    </S.Wrapper>
  );
};

SearchAutocomplete.propTypes = {
  // Search-specific props
  onSearch: PropTypes.func,
  fetchOptions: PropTypes.func,
  debounceTime: PropTypes.number,

  // Standard Autocomplete props
  multiple: PropTypes.bool,
  label: PropTypes.string,
  testId: PropTypes.string,
  placeholder: PropTypes.string,
  clearable: PropTypes.bool,
  width: PropTypes.string,
  height: PropTypes.oneOf(['LARGE', 'MEDIUM', 'SMALL', 'TINY', 'DYNAMIC', PropTypes.string, PropTypes.number]),
  minHeight: PropTypes.oneOf(['LARGE', 'MEDIUM', 'SMALL', 'TINY', PropTypes.string, PropTypes.number]),
  maxPopperHeight: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onInputChange: PropTypes.func,
  onClose: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.arrayOf(PropTypes.string)]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      disabled: PropTypes.bool,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      icon: PropTypes.oneOfType([PropTypes.node, PropTypes.element]),
      dotColor: PropTypes.string,
      children: PropTypes.array
    })
  ),
  disablePortal: PropTypes.bool,
  isOptionEqualToValue: PropTypes.func,
  filterOptions: PropTypes.func,
  clearOnBlur: PropTypes.bool,
  blurOnSelect: PropTypes.bool,
  noOptionsText: PropTypes.string,
  freeSolo: PropTypes.bool,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf([Themes.DARK, Themes.LIGHT]),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  withSearchIcon: PropTypes.bool
};

export default SearchAutocomplete;
